import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Plus } from "lucide-react";
import { useStore } from "@/store/StoreContext";
import { img, money } from "@/lib/api";
import DropCountdown from "@/components/DropCountdown";

const STATUS_STYLE = {
  limited: "bg-gold text-noir-bg",
  sold_out: "bg-cream/10 text-cream/60 border border-cream/20",
  coming_soon: "bg-transparent text-gold border border-gold/50",
  restock: "bg-cream text-noir-bg",
};

export default function ProductCard({ product, index = 0 }) {
  const { addToCart, wishlist, toggleWishlist } = useStore();
  const navigate = useNavigate();
  const wished = wishlist.includes(product.slug);
  const buyable = product.status === "available" || product.status === "limited" || product.status === "restock";

  const quickAdd = (e) => {
    e.preventDefault();
    if (product.sizes.length === 1) {
      addToCart(product, product.sizes[0]);
    } else {
      navigate(`/product/${product.slug}`);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.7, delay: (index % 4) * 0.08, ease: [0.16, 1, 0.3, 1] }}
      className="group"
      data-testid={`product-card-${product.slug}`}
    >
      <Link to={`/product/${product.slug}`} className="block">
        <div className="spotlight-card relative aspect-[3/4] overflow-hidden bg-noir-card">
          <img
            src={img(product.images[0])}
            alt={product.name}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-[1.2s] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.06]"
          />
          {product.badge && (
            <span className={`absolute top-3 left-3 font-mono2 text-[9px] tracking-[0.25em] uppercase px-2.5 py-1.5 ${STATUS_STYLE[product.status] || "bg-noir-bg text-cream"}`} data-testid={`product-badge-${product.slug}`}>
              {product.badge}
            </span>
          )}
          <button
            onClick={(e) => { e.preventDefault(); toggleWishlist(product); }}
            className={`absolute top-3 right-3 p-2 transition-colors duration-300 ${wished ? "text-gold" : "text-cream/50 hover:text-gold"}`}
            data-testid={`product-card-wishlist-toggle-${product.slug}`}
            aria-label="Toggle wishlist"
          >
            <Heart size={16} strokeWidth={1.5} fill={wished ? "currentColor" : "none"} />
          </button>
          {buyable && (
            <button
              onClick={quickAdd}
              className="absolute bottom-0 left-0 right-0 bg-noir-bg/90 backdrop-blur-sm border-t border-white/10 py-3.5 font-mono2 text-[10px] tracking-[0.3em] uppercase text-cream flex items-center justify-center gap-2 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] hover:text-gold"
              data-testid={`product-card-add-to-cart-${product.slug}`}
            >
              <Plus size={13} /> {product.sizes.length === 1 ? "Quick Add" : "Select Size"}
            </button>
          )}
        </div>
        <div className="pt-4 flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-sm sm:text-base font-semibold uppercase tracking-wide group-hover:text-gold transition-colors duration-300">{product.name}</h3>
            <p className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-cream/40 mt-1">{product.serial} — {product.category}</p>
            {["limited", "restock"].includes(product.status) && product.units > 0 && (
              <p className="font-mono2 text-[9px] tracking-[0.2em] uppercase text-gold/80 mt-1" data-testid={`stock-left-${product.slug}`}>Only {product.units} left</p>
            )}
          </div>
          <span className="font-mono2 text-sm text-gold whitespace-nowrap">{money(product.price)}</span>
        </div>
        {product.status === "coming_soon" && <DropCountdown className="block mt-2" />}
      </Link>
    </motion.div>
  );
}
